import { useCallback, useEffect, useRef, useState } from 'react';
import ApiService from '../services/api';

// Global cache for hooks to prevent multiple requests for same data
const hookCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const refreshTriggers = new Map(); // Track refresh triggers for each cache key

// Export functions to clear hook cache
export const clearHookCache = (cacheKey) => {
  if (cacheKey) {
    hookCache.delete(cacheKey);
    // Increment refresh trigger to force refetch
    const currentTrigger = refreshTriggers.get(cacheKey) || 0;
    refreshTriggers.set(cacheKey, currentTrigger + 1);
    console.log(`Hook cache cleared for: ${cacheKey}`);
  } else {
    hookCache.clear();
    // Increment all refresh triggers
    refreshTriggers.forEach((_, key) => {
      refreshTriggers.set(key, (refreshTriggers.get(key) || 0) + 1);
    });
    console.log('All hook cache cleared');
  }
};

export const clearAllHookCache = () => {
  hookCache.clear();
  refreshTriggers.forEach((_, key) => {
    refreshTriggers.set(key, (refreshTriggers.get(key) || 0) + 1);
  });
  console.log('All hook cache cleared');
};

// Make functions available globally for AdminContext
if (typeof window !== 'undefined') {
  window.clearHookCache = clearHookCache;
  window.clearAllHookCache = clearAllHookCache;
}

// Custom hook for managing API state
export const useApi = (apiCall, dependencies = [], cacheKey = null) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);
  const retryCountRef = useRef(0);
  const maxRetries = 3;

  const finalCacheKey = cacheKey || JSON.stringify(dependencies);
  const refreshTrigger = refreshTriggers.get(finalCacheKey) || 0;

  useEffect(() => {
    // If refresh trigger changed, force refetch by clearing cache and resetting fetch flag
    if (refreshTrigger > 0) {
      if (finalCacheKey && hookCache.has(finalCacheKey)) {
        hookCache.delete(finalCacheKey);
      }
      hasFetchedRef.current = false;
    }

    // Check global cache first (only if refresh trigger hasn't changed)
    if (refreshTrigger === 0 && finalCacheKey && hookCache.has(finalCacheKey)) {
      const cached = hookCache.get(finalCacheKey);
      if (Date.now() - cached.timestamp < CACHE_DURATION) {
        setData(cached.data);
        setLoading(false);
        setError(null);
        console.log(`Hook cache hit for: ${finalCacheKey}`);
        return;
      } else {
        hookCache.delete(finalCacheKey);
      }
    }

    // Prevent duplicate calls unless it's a retry or refresh trigger changed
    if (hasFetchedRef.current && retryCountRef.current === 0 && refreshTrigger === 0) return;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiCall();
        setData(result);
        hasFetchedRef.current = true;
        retryCountRef.current = 0; // Reset retry count on success
        
        // Cache the result
        if (finalCacheKey) {
          hookCache.set(finalCacheKey, {
            data: result,
            timestamp: Date.now()
          });
          console.log(`Hook cached: ${finalCacheKey}`);
        }
      } catch (err) {
        console.error(`API call failed for ${finalCacheKey}:`, err);
        
        // Only retry if we haven't exceeded max retries
        if (retryCountRef.current < maxRetries) {
          retryCountRef.current += 1;
          console.log(`Retrying API call for ${finalCacheKey}, attempt ${retryCountRef.current}`);
          
          // Exponential backoff: wait longer between retries
          const delay = Math.pow(2, retryCountRef.current) * 1000;
          setTimeout(() => {
            hasFetchedRef.current = false;
            fetchData();
          }, delay);
        } else {
          setError(err.message || 'An error occurred');
          hasFetchedRef.current = true; // Stop retrying after max attempts
        }
      } finally {
        if (retryCountRef.current === 0 || retryCountRef.current >= maxRetries) {
          setLoading(false);
        }
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalCacheKey, refreshTrigger]); // Include refreshTrigger to refetch when cache is cleared

  const refetch = async () => {
    try {
      setLoading(true);
      setError(null);
      retryCountRef.current = 0; // Reset retry count for manual refetch
      hasFetchedRef.current = false; // Allow refetch
      const result = await apiCall();
      setData(result);
      hasFetchedRef.current = true;
      
      // Update cache
      if (finalCacheKey) {
        hookCache.set(finalCacheKey, {
          data: result,
          timestamp: Date.now()
        });
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, refetch };
};

// Hook for products
export const useProducts = (filters = {}) => {
  const serializedFilters = JSON.stringify(filters);
  const apiCall = useCallback(() => ApiService.getAllProducts(filters), [filters]);
  return useApi(apiCall, [serializedFilters]);
};

// Hook for single product
export const useProduct = (id) => {
  const apiCall = useCallback(() => ApiService.getProductById(id), [id]);
  return useApi(apiCall, [id]);
};

// Hook for products by category
export const useProductsByCategory = (category, filters = {}) => {
  const serializedFilters = JSON.stringify(filters);
  const apiCall = useCallback(
    () => ApiService.getProductsByCategory(category, filters),
    [category, filters]
  );
  return useApi(apiCall, [category, serializedFilters]);
};

// Hook for categories
export const useCategories = () => {
  const apiCall = useCallback(() => ApiService.getCategories(), []);
  return useApi(apiCall, [], 'categories');
};

// Hook for featured products
export const useFeaturedProducts = () => {
  const apiCall = useCallback(() => ApiService.getFeaturedProducts(), []);
  return useApi(apiCall, [], 'featured-products');
};

// Hook for new arrivals
export const useNewArrivals = () => {
  const apiCall = useCallback(() => ApiService.getNewArrivals(), []);
  return useApi(apiCall, [], 'new-arrivals');
};

// Hook for best sellers
export const useBestSellers = () => {
  const apiCall = useCallback(() => ApiService.getBestSellers(), []);
  return useApi(apiCall, [], 'best-sellers');
};

// Hook for festive specials
export const useFestiveSpecials = () => {
  const apiCall = useCallback(() => ApiService.getFestiveSpecials(), []);
  return useApi(apiCall, [], 'festive-specials');
};

// Hook for featured templates
export const useFeaturedTemplates = () => {
  const apiCall = useCallback(() => ApiService.getFeaturedTemplatesForLanding(), []);
  return useApi(apiCall, [], 'featured-templates');
};

// Hook for inhouse publications
export const useInhousePublications = () => {
  const apiCall = useCallback(() => ApiService.getInhousePublications(), []);
  return useApi(apiCall, [], 'inhouse-publications');
};

// Hook for hot selling
export const useHotSelling = () => {
  const apiCall = useCallback(() => ApiService.getHotSelling(), []);
  return useApi(apiCall, [], 'hot-selling');
};

// Hook for search
export const useSearch = (query, filters = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const search = async (searchQuery, searchFilters = {}) => {
    if (!searchQuery.trim()) {
      setData(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await ApiService.searchProducts(searchQuery, searchFilters);
      setData(result);
    } catch (err) {
      setError(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const serializedFilters = JSON.stringify(filters);

  useEffect(() => {
    if (query) {
      search(query, filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, serializedFilters]);

  return { data, loading, error, search };
};

// Hook for cart management
export const useCart = () => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCart = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await ApiService.getCart();
      setCart(result);
    } catch (err) {
      setError(err.message || 'Failed to fetch cart');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId, quantity = 1, customization = {}) => {
    try {
      setLoading(true);
      setError(null);
      await ApiService.addToCart(productId, quantity, customization);
      await fetchCart(); // Refresh cart after adding
    } catch (err) {
      setError(err.message || 'Failed to add to cart');
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    try {
      setLoading(true);
      setError(null);
      await ApiService.updateCartItem(itemId, quantity);
      await fetchCart(); // Refresh cart after updating
    } catch (err) {
      setError(err.message || 'Failed to update cart item');
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      setLoading(true);
      setError(null);
      await ApiService.removeFromCart(itemId);
      await fetchCart(); // Refresh cart after removing
    } catch (err) {
      setError(err.message || 'Failed to remove from cart');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  return {
    cart,
    loading,
    error,
    addToCart,
    updateCartItem,
    removeFromCart,
    refetchCart: fetchCart
  };
};
